/**
 * Tool Library — the gallery, then that tool's configuration, in one dialog.
 *
 *   gallery (grid of tools)  →  pick one  →  configure it  →  Add to map
 *
 * Deliberately the same two-step shape as Add Data: pick a thing, then say how
 * it should behave, without leaving the dialog. An admin adding a Search should
 * be asked which layers and which fields there and then — that is the moment
 * they know the answer, and it is how Esri asks it.
 *
 * The form is rendered FROM the registry's `configFields`, so a new tool needs
 * a registry entry and nothing here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Search as SearchIcon, X } from 'lucide-react';
import Tooltip from '../glass/Tooltip';
import {
  TOOL_CATEGORIES,
  TOOL_REGISTRY,
  toolConfigWithDefaults,
  visibleFields,
  type ToolDefinition,
  type ToolField,
} from '../../tools/toolRegistry';
import type { Layer } from '../../types';
import { hasToolPanel } from './panels';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Open directly on this tool's settings instead of the gallery.
   *
   * How the ribbon's per-tool gear gets here: someone looking at a tool wants
   * that tool's form, not a gallery they then have to search.
   */
  initialToolId?: string | null;
  /** Tools already on the map; they appear ticked and cannot be added twice. */
  enabledToolIds: string[];
  /** Existing settings, so re-opening a tool shows how it was set up. */
  toolConfigs: Record<string, Record<string, unknown>>;
  /** The map's layers, for the `layers` and `fields` field kinds. */
  layers: Layer[];
  /** Field names per layer id, resolved by the caller from the services. */
  fieldsByLayer: Record<string, string[]>;
  onAdd: (toolId: string, config: Record<string, unknown>) => void;
  onRemove: (toolId: string) => void;
}

export default function AddToolDialog({
  open,
  onClose,
  initialToolId = null,
  enabledToolIds,
  toolConfigs,
  layers,
  fieldsByLayer,
  onAdd,
  onRemove,
}: Props) {
  const [selected, setSelected] = useState<ToolDefinition | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [query, setQuery] = useState('');

  const openTool = useCallback(
    (tool: ToolDefinition) => {
      setSelected(tool);
      setDraft(toolConfigWithDefaults(tool.id, toolConfigs[tool.id]));
    },
    [toolConfigs],
  );

  /**
   * Jumps to the requested tool once per opening.
   *
   * The guard is not defensive tidiness. Saving settings rewrites
   * `toolConfigs`, which changes `openTool`'s identity and re-runs this effect
   * — without the ref it would drag the user straight back into the form they
   * just saved and closed.
   */
  const jumpedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      jumpedRef.current = null;
      return;
    }
    if (jumpedRef.current === (initialToolId ?? '')) return;
    jumpedRef.current = initialToolId ?? '';

    if (!initialToolId) {
      setSelected(null);
      setDraft({});
      return;
    }
    const tool = TOOL_REGISTRY.find((entry) => entry.id === initialToolId);
    if (tool) openTool(tool);
  }, [open, initialToolId, openTool]);

  const back = () => {
    setSelected(null);
    setDraft({});
  };

  const close = () => {
    back();
    setQuery('');
    onClose();
  };

  /* Only tools that actually have a panel. The registry is the catalogue of
     what BlueView intends to offer; `hasToolPanel` is what it can currently
     open. Twelve entries used to be addable and then opened onto an empty tab,
     which read as a broken app rather than an unfinished one. */
  const buildable = useMemo(() => TOOL_REGISTRY.filter((tool) => hasToolPanel(tool.id)), []);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return buildable;
    return buildable.filter(
      (tool) =>
        tool.name.toLowerCase().includes(needle) ||
        tool.description.toLowerCase().includes(needle),
    );
  }, [query, buildable]);

  /* Fields offered by the layers the admin picked. Falls back to every layer's
     fields when none are chosen, because "no layers" means "all layers". */
  /* Memoised: a fresh array literal every render would make the field list
     below recompute on every keystroke. */
  const chosenLayerIds = useMemo(
    () => (Array.isArray(draft.layers) ? (draft.layers as string[]) : []),
    [draft.layers],
  );
  const availableFields = useMemo(() => {
    const source = chosenLayerIds.length > 0 ? chosenLayerIds : layers.map((layer) => layer.id);
    const names = new Set<string>();
    for (const id of source) for (const name of fieldsByLayer[id] ?? []) names.add(name);
    return Array.from(names).sort();
  }, [chosenLayerIds, layers, fieldsByLayer]);

  /** True while the panel is reopening a tool the map already has. */
  const editing = selected !== null && enabledToolIds.includes(selected.id);

  if (!open) return null;

  const setValue = (key: string, value: unknown) =>
    setDraft((current) => ({ ...current, [key]: value }));

  /**
   * The Select all / Clear all line above a checkbox list.
   *
   * Also the only place that explains the difference between the two ways of
   * saying "everything": an EMPTY list means every item, now and in future, so
   * a layer added tomorrow is included automatically. Ticking every box pins
   * the list to what exists today. Both are legitimate; which one someone wants
   * is not guessable, so the panel says what each does rather than choosing.
   */
  const SelectAllBar = ({
    total,
    chosen,
    onSelectAll,
    onClear,
    noun,
  }: {
    total: number;
    chosen: number;
    onSelectAll: () => void;
    onClear: () => void;
    noun: string;
  }) => {
    if (total === 0) return null;
    return (
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-500">
          {chosen === 0 ? (
            <>
              <span className="font-semibold text-slate-600">All {total}</span> {noun} — nothing
              pinned
            </>
          ) : (
            <>
              <span className="font-semibold text-slate-600 tabular-nums">
                {chosen} of {total}
              </span>{' '}
              {noun}
            </>
          )}
        </span>
        <button
          type="button"
          onClick={chosen > 0 ? onClear : onSelectAll}
          className="flex-shrink-0 text-[11px] font-semibold text-blue-600 transition hover:text-blue-800"
        >
          {chosen > 0 ? 'Clear all' : 'Select all'}
        </button>
      </div>
    );
  };

  const renderField = (field: ToolField) => {
    const value = draft[field.key];

    switch (field.kind) {
      case 'layers':
        return (
          <>
            <SelectAllBar
              total={layers.length}
              chosen={chosenLayerIds.length}
              noun="layers"
              onSelectAll={() => setValue('layers', layers.map((layer) => layer.id))}
              onClear={() => setValue('layers', [])}
            />
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
            {layers.length === 0 && (
              <p className="text-[11px] text-slate-400">Add layers to the map first.</p>
            )}
            {layers.map((layer) => {
              const checked = chosenLayerIds.includes(layer.id);
              return (
                <label
                  key={layer.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setValue(
                        'layers',
                        checked
                          ? chosenLayerIds.filter((id) => id !== layer.id)
                          : [...chosenLayerIds, layer.id],
                      )
                    }
                    className="h-3.5 w-3.5 accent-blue-600"
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                    {layer.displayName?.trim() || layer.name}
                  </span>
                </label>
              );
            })}
            </div>
          </>
        );

      case 'fields': {
        const chosen = Array.isArray(value) ? (value as string[]) : [];
        return (
          <>
            <SelectAllBar
              total={availableFields.length}
              chosen={chosen.length}
              noun="fields"
              onSelectAll={() => setValue(field.key, [...availableFields])}
              onClear={() => setValue(field.key, [])}
            />
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
            {availableFields.length === 0 && (
              <p className="text-[11px] text-slate-400">
                No fields reported yet — pick a layer above, or leave this empty for all fields.
              </p>
            )}
            {availableFields.map((name) => {
              const checked = chosen.includes(name);
              return (
                <label
                  key={name}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setValue(
                        field.key,
                        checked ? chosen.filter((f) => f !== name) : [...chosen, name],
                      )
                    }
                    className="h-3.5 w-3.5 accent-blue-600"
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{name}</span>
                </label>
              );
            })}
            </div>
          </>
        );
      }

      case 'multiselect': {
        const chosen = Array.isArray(value) ? (value as string[]) : [];
        const options = field.options ?? [];
        return (
          <>
            <SelectAllBar
              total={options.length}
              chosen={chosen.length}
              noun="options"
              onSelectAll={() => setValue(field.key, options.map((option) => option.value))}
              onClear={() => setValue(field.key, [])}
            />
            <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-2">
            {options.map((option) => {
              const checked = chosen.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-white"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setValue(
                        field.key,
                        checked
                          ? chosen.filter((entry) => entry !== option.value)
                          : [...chosen, option.value],
                      )
                    }
                    className="h-3.5 w-3.5 accent-blue-600"
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                    {option.label}
                  </span>
                </label>
              );
            })}
            </div>
          </>
        );
      }

      case 'boolean':
        return (
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) => setValue(field.key, event.target.checked)}
              className="h-3.5 w-3.5 accent-blue-600"
            />
            <span className="text-xs text-slate-600">Enabled</span>
          </label>
        );

      case 'number':
        return (
          <input
            type="number"
            min={field.min}
            max={field.max}
            value={value === undefined ? '' : String(value)}
            onChange={(event) => setValue(field.key, Number(event.target.value))}
            className="gl-input w-full px-2.5 py-1.5 text-xs"
          />
        );

      case 'select':
        return (
          <select
            value={value === undefined ? '' : String(value)}
            onChange={(event) => setValue(field.key, event.target.value)}
            className="gl-input w-full px-2.5 py-1.5 text-xs"
          >
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            value={value === undefined ? '' : String(value)}
            onChange={(event) => setValue(field.key, event.target.value)}
            className="gl-input w-full px-2.5 py-1.5 text-xs"
          />
        );
    }
  };

  return (
    <div
      className="gl-modal-backdrop fixed inset-0 z-[9000] flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        role="dialog"
        aria-label="Tool Library"
        onClick={(event) => event.stopPropagation()}
        className="gl-modal flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden"
        style={{ borderRadius: 'var(--glass-radius-lg)' }}
      >
        <header className="gl-header flex flex-shrink-0 items-center gap-2 px-5 py-3">
          {selected && (
            <button
              type="button"
              onClick={back}
              aria-label="Back to all tools"
              className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-[#0F2942]">
              {selected ? selected.name : 'Tool Library'}
            </h2>
            <p className="truncate text-[11px] text-slate-500">
              {selected
                ? editing
                  ? 'On the map already — change how it is set up.'
                  : selected.description
                : 'Choose a tool, then set it up for this map.'}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!selected ? (
            <>
              <div className="relative mb-3">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search tools…"
                  aria-label="Search tools"
                  className="gl-input w-full py-2 pl-8 pr-3 text-sm"
                />
              </div>

              {TOOL_CATEGORIES.map((category) => {
                const group = matches.filter((tool) => tool.category === category);
                if (group.length === 0) return null;
                return (
                  <section key={category} className="mb-2.5">
                    <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {category}
                    </h3>
                    <div className="grid grid-cols-3 gap-1">
                      {group.map((tool) => {
                        const Icon = tool.icon;
                        const added = enabledToolIds.includes(tool.id);
                        return (
                          /* The name is repeated as the tooltip title because a
                             long one ("Coordinates readout") truncates in a row
                             this narrow. */
                          <Tooltip
                            key={tool.id}
                            title={tool.name}
                            content={
                              added
                                ? `Already on the map. Change its settings from the gear on its ribbon button.`
                                : tool.description
                            }
                          >
                            <button
                              type="button"
                              onClick={() => openTool(tool)}
                              aria-label={
                                added ? `${tool.name} is already on the map` : `Add ${tool.name} to the map`
                              }
                              className={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-left transition ${
                                added
                                  ? 'border-blue-300 bg-blue-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              <span
                                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${
                                  added ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                <Icon className="h-3 w-3" />
                              </span>
                              <span className="min-w-0 flex-1 truncate text-[10.5px] font-medium leading-tight text-slate-700">
                                {tool.name}
                              </span>
                              {added && <Check className="h-3 w-3 flex-shrink-0 text-blue-600" />}
                            </button>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </section>
                );
              })}

              {matches.length === 0 && (
                <p className="py-8 text-center text-sm text-slate-400">
                  Nothing matches “{query.trim()}”.
                </p>
              )}
            </>
          ) : (
            <div className="space-y-3">
              {visibleFields(selected, draft).length === 0 ? (
                <p className="rounded-lg bg-slate-50 px-3 py-3 text-xs text-slate-500">
                  {editing
                    ? 'This tool has nothing to configure. Remove it below if you no longer want it.'
                    : 'This tool has nothing to configure — add it and it will appear on the map.'}
                </p>
              ) : (
                visibleFields(selected, draft).map((field) => (
                  <label key={field.key} className="block">
                    <span className="text-xs font-semibold text-slate-600">{field.label}</span>
                    {field.hint && (
                      <span className="mt-0.5 block text-[11px] leading-snug text-slate-400">
                        {field.hint}
                      </span>
                    )}
                    <div className="mt-1">{renderField(field)}</div>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        <footer className="gl-footer flex flex-shrink-0 items-center justify-between gap-2 px-5 py-3">
          {editing ? (
            <button
              type="button"
              onClick={() => {
                onRemove(selected.id);
                back();
              }}
              className="gl-btn gl-btn--secondary px-3 py-2 text-sm"
            >
              Remove from map
            </button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <button type="button" onClick={close} className="gl-btn gl-btn--secondary px-3.5 py-2 text-sm">
              Cancel
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => {
                  onAdd(selected.id, draft);
                  back();
                }}
                className="gl-btn gl-btn--primary px-3.5 py-2 text-sm"
              >
                <Check className="h-4 w-4" />
                {editing ? 'Save settings' : 'Add to map'}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
